import { alpha, Box, ButtonBase, darken, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getGlassCtaButtonSx } from '../../common/GlassCtaButton';
import AnimatedTypography from '../../core/AnimatedTypography';
import Details from '../components/Details';
import TourSelector from '../components/TourSelector';
import ITour from '../models/ITour';
import TourService from '../services/TourService';

function glassPriceToggleTrackSx(theme: Theme) {
    const pm = theme.palette.primary.main;
    const isDark = theme.palette.mode === 'dark';
    return {
        display: 'flex',
        flexDirection: 'row' as const,
        gap: 0.5,
        width: '100%',
        p: 0.5,
        mb: 1.5,
        borderRadius: 999,
        border: `1px solid ${alpha(pm, 0.35)}`,
        background: `linear-gradient(135deg, ${alpha(pm, isDark ? 0.14 : 0.08)} 0%, ${alpha(pm, isDark ? 0.05 : 0.03)} 100%)`,
        backdropFilter: 'blur(12px)',
        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, isDark ? 0.3 : 0.07)}`,
    };
}

function glassPriceToggleSelectedSx(theme: Theme) {
    const glass = getGlassCtaButtonSx(theme);
    const gh = glass['&:hover'] as {
        borderColor?: string;
        background?: string;
        boxShadow?: string;
    };
    return {
        flex: 1,
        borderRadius: 999,
        py: 0.65,
        px: 0.75,
        display: 'block',
        textAlign: 'center' as const,
        transition: 'background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        border: glass.border as string,
        background: glass.background as string,
        boxShadow: glass.boxShadow as string,
        '&:hover': {
            borderColor: gh.borderColor,
            background: gh.background,
            boxShadow: gh.boxShadow,
        },
    };
}

function glassPriceToggleUnselectedSx(theme: Theme) {
    const pm = theme.palette.primary.main;
    return {
        flex: 1,
        borderRadius: 999,
        py: 0.65,
        px: 0.75,
        display: 'block',
        textAlign: 'center' as const,
        transition: 'background 0.2s ease, border-color 0.2s ease',
        border: '1px solid transparent',
        background: 'transparent',
        '&:hover': {
            background: alpha(pm, theme.palette.mode === 'dark' ? 0.1 : 0.06),
            borderColor: alpha(pm, 0.22),
        },
    };
}

function glassToggleLabelSx(selected: boolean) {
    return {
        fontWeight: selected ? 700 : 600,
        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
        letterSpacing: 0.015,
        color: selected ? 'text.primary' : 'text2.secondary',
        textAlign: 'center' as const,
        width: '100%',
        textTransform: 'none' as const,
        display: 'block',
    };
}

function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
}

const service = new TourService();

type PriceOption = 'withBike' | 'ownBike';

const TourDetailsPage: React.FC = () => {
    const [tourId, setTourId] = useState(2);
    const [tour, setTour] = useState<ITour | null>(null);
    const [tours, setTours] = useState<ITour[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [priceOption, setPriceOption] = useState<PriceOption>('withBike');
    const { t } = useTranslation();
    const query = useQuery();

    const handleChange = (newValue: number) => {
        setSearchParams({ tourId: `${newValue}` });
        setTourId(newValue);
    };

    React.useEffect(() => {
        const list = service.getTours();
        setTours(list);
        const id = query.get('tourId');
        if (id && (id === '1' || id === '2')) {
            setTourId(Number(id));
            const selected = list?.find((t) => t.id === Number(id));
            if (selected) setTour(selected);
        }
    }, []);

    React.useEffect(() => {
        const selected = tours?.find((t) => t.id === tourId) ?? null;
        setTour(selected);
    }, [tours, tourId]);

    useEffect(() => {
        window?.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const isWithBike = priceOption === 'withBike';
    const price = isWithBike ? (tour?.price ?? '–') : (tour?.priceWithOwnMotorcycle ?? '–');
    const lastLine = isWithBike
        ? t('tourDetails.common.enduroIncluded')
        : t('tourDetails.common.enduroExcluded');

    return (
        <Box mt={{ xs: 8, sm: 10 }} pb={8}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>
                    {tourId === 2 ? t("seo.tours.title8Days") : t("seo.tours.title5Days")}
                </title>
                <meta
                    name="description"
                    content={tourId === 2 ? t("seo.tours.description8Days") : t("seo.tours.description5Days")}
                />
                <link
                    rel="canonical"
                    href={
                        tourId === 2
                            ? 'https://enduro-connection-radusa.com/tours?tourId=2'
                            : 'https://enduro-connection-radusa.com/tours?tourId=1'
                    }
                />
                <meta property="og:title" content={tourId === 2 ? t("seo.tours.title8Days") : t("seo.tours.title5Days")} />
                <meta property="og:description" content={t("seo.tours.ogDescription")} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://enduro-connection-radusa.com/tours" />
            </Helmet>
            <AnimatedTypography variant="h3" color="text2.primary" fontWeight={600} mb={2}>
                {t('tourDetails.title')}
            </AnimatedTypography>

            <TourSelector tours={tours} selectedValue={tourId} onChange={handleChange} />

            {/* Two-column: price card (left) + description (right) on desktop */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '500px 1fr' },
                    gap: { xs: 4, md: 2.5 },
                    mt: 4,
                    alignItems: { xs: 'stretch', md: 'center' },
                }}
            >
                {/* Left: single price card with toggle – sticky on desktop */}
                <Box
                    sx={{
                        position: { md: 'sticky' },
                        top: { md: 80 },
                    }}
                >
                    <Box
                        sx={(theme) => ({
                            p: 2.5,
                            borderRadius: 3,
                            background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.5)} 100%)`,
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)}`,
                        })}
                    >
                        {/* Toggle: S našim / S vlastitim — glass CTA kao bazni button */}
                        <Box sx={(theme) => glassPriceToggleTrackSx(theme as Theme)}>
                            <ButtonBase
                                focusRipple
                                aria-pressed={priceOption === 'withBike'}
                                onClick={() => setPriceOption('withBike')}
                                sx={(theme) =>
                                    priceOption === 'withBike'
                                        ? glassPriceToggleSelectedSx(theme as Theme)
                                        : glassPriceToggleUnselectedSx(theme as Theme)
                                }
                            >
                                <Typography
                                    component="span"
                                    variant="body2"
                                    sx={glassToggleLabelSx(priceOption === 'withBike')}
                                >
                                    {t('tourDetails.common.priceOptionWithBike')}
                                </Typography>
                            </ButtonBase>
                            <ButtonBase
                                focusRipple
                                aria-pressed={priceOption === 'ownBike'}
                                onClick={() => setPriceOption('ownBike')}
                                sx={(theme) =>
                                    priceOption === 'ownBike'
                                        ? glassPriceToggleSelectedSx(theme as Theme)
                                        : glassPriceToggleUnselectedSx(theme as Theme)
                                }
                            >
                                <Typography
                                    component="span"
                                    variant="body2"
                                    sx={glassToggleLabelSx(priceOption === 'ownBike')}
                                >
                                    {t('tourDetails.common.priceOptionOwnBike')}
                                </Typography>
                            </ButtonBase>
                        </Box>

                        <Typography
                            variant="subtitle2"
                            color="text2.primary"
                            sx={{ mb: 2, textAlign: 'center' }}
                        >
                            {tourId === 2 && t('tourDetails.common.tour8DaysLabel')}
                            {tourId === 1 && t('tourDetails.common.tour5DaysLabel')}
                        </Typography>

                        <Typography variant="overline" color="text2.primary" fontWeight={600}>
                            {t('tourDetails.common.price')}
                        </Typography>
                        <Typography variant="h3" fontWeight={800} color="secondary.main" sx={{ mt: 0.25 }}>
                            €{price}
                        </Typography>
                        <Box component="ul" sx={{ m: 0, mt: 1.5, pl: 2.5, color: 'text2.primary' }}>
                            <Typography component="li" variant="body2">
                                {t('tourDetails.common.pricePerPerson')}
                            </Typography>
                            <Typography variant="caption"
                                sx={{
                                    p: "4px",
                                    px: "6px",
                                    mb: 0.5,
                                    borderRadius: "6px",
                                    backgroundColor: (theme) => darken(theme.palette.background.default, 0.5),
                                    fontStyle: 'italic'
                                }}>
                                *{t('tourDetails.common.singleBedroomPricing', { amount: tour?.id === 2 ? 80 : 50 })}
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                {t('tourDetails.common.withBreakfast')}
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                {lastLine}
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                {t('tourDetails.common.motorWashing')}
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                {t('tourDetails.common.drinkingWater')}
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                {t('tourDetails.common.chainSpray')}
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                {t('tourDetails.common.fuelIncluded')}
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                {t('tourDetails.common.tourGuide')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Right: description – plain text */}
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        component="div"
                        color="text2.primary"
                        sx={{
                            whiteSpace: 'pre-line',
                            lineHeight: 1.5,
                            fontSize: '1rem',
                        }}
                    >
                        {t(tour?.description ?? '')}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ mt: 6 }}>
                <Details tour={tour} />
            </Box>
        </Box>
    );
};

export default TourDetailsPage;
